import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { SetURLSearchParams } from "react-router-dom";
import LinearProgress from "@mui/material/LinearProgress";
import { ICandidateFilter, ICandidateList } from "../../data/Candidates";
import CandidateListTableRow from "./CandidateListTableRow";

type Order = "asc" | "desc";

export interface ICandidateListTableProps {
  candidates?: ICandidateList;
  isLoading: boolean;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  filter: ICandidateFilter;
}

const CandidateListTable = ({
  candidates,
  isLoading,
  searchParams,
  setSearchParams,
  filter,
}: ICandidateListTableProps) => {
  const pageSize = Number(searchParams.get("page_size") ?? 10);
  const page = Math.max(Number(searchParams.get("page")) - 1, 0);
  const ordering = searchParams.get("ordering") || "-modified";
  const order: Order = ordering[0] === "-" ? "desc" : "asc";
  const orderBy: string =
    ordering[0] === "-" ? ordering.substring(1) : ordering;

  const createSortHandler = (property: string) => () => {
    const isAsc = orderBy === property && order === "asc";
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("ordering", isAsc ? `-${property}` : property);
      return params;
    });
  };

  const handlePageChange = (_event: React.SyntheticEvent | null, newPage: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("page", (newPage + 1).toString());
      return params;
    });
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("page_size", value.toString());
      params.set("page", "1");
      return params;
    });
  };

  const emptyRows = pageSize - (candidates?.results?.length ?? 0);

  return (
    <>
      <TableContainer component="div">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="left" width="110px" />
              <TableCell
                align="left"
                width="20%"
                sortDirection={orderBy === "modified" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "modified"}
                  direction={orderBy === "modified" ? order : "asc"}
                  onClick={createSortHandler("modified")}
                >
                  Last Edited
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="left"
                // width="50%"
                sortDirection={orderBy === "name" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={orderBy === "name" ? order : "asc"}
                  onClick={createSortHandler("name")}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell align="left" width="20%">
                Nationality
              </TableCell>
              <TableCell
                align="left"
                width="20%"
                sortDirection={orderBy === "salary" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "salary"}
                  direction={orderBy === "salary" ? order : "asc"}
                  onClick={createSortHandler("salary")}
                >
                  Salary
                </TableSortLabel>
              </TableCell>
              <TableCell key="actions" align="right" width="90px" />
            </TableRow>
          </TableHead>
          <TableBody>
            {candidates?.results.map((candidate) => (
              <CandidateListTableRow
                key={candidate.id}
                candidate={candidate}
                filter={filter}
              />
            ))}
            {emptyRows > 0 && (
              <TableRow style={{ height: `${67 * emptyRows}px` }}>
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {isLoading ? <LinearProgress sx={{ mt: -0.5 }} /> : null}
      <TablePagination
        rowsPerPageOptions={[5, 10, 15, 20]}
        component="div"
        count={candidates?.count ?? 0}
        rowsPerPage={pageSize}
        page={candidates?.count ? page : 0}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
      />
    </>
  );
};

export default CandidateListTable;
